import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { CustomFieldDefinition } from '../../../../master-data-management'
import { SupplierView } from './Supplier.view'

export type FormValues = {
  customFieldDefinitions: CustomFieldDefinition[] | undefined
}
export type FormFieldName = keyof CustomFieldDefinition

type Props = {
  customFieldDefinitions: CustomFieldDefinition[] | undefined
  isLoading: boolean
  hasLoadingRowId: (rowId: string) => boolean
  isBulkDeleteRunning: boolean
  handleCreate: (customFieldDefinition: CustomFieldDefinition) => Promise<void>
  handleDelete: (
    customFieldDefinitions: CustomFieldDefinition[],
    callback?: () => void,
  ) => Promise<void>
}

export const SupplierForm: React.FC<Props> = ({
  customFieldDefinitions,
  isLoading,
  hasLoadingRowId,
  isBulkDeleteRunning,
  handleCreate,
  handleDelete,
}) => {
  const { t } = useTranslation()

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      customFieldDefinitions: customFieldDefinitions,
    },
    validate: formData => {
      let errors: any = []

      const keys =
        formData.customFieldDefinitions?.map(customFieldDefinition =>
          customFieldDefinition.Key.toLowerCase(),
        ) || []
      const duplicates = keys.filter(
        (item, index) => keys.indexOf(item) !== index,
      )

      formData.customFieldDefinitions?.forEach((data, index) => {
        if (!errors[index]) {
          errors[index] = {}
        }

        if (!data.Key) {
          errors[index] = {
            ...errors[index],
            Key: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.batchnumber.errors.required',
            ),
          }
        } else if (duplicates?.includes(data.Key.toLowerCase())) {
          errors[index] = {
            ...errors[index],
            Key: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.batchnumber.errors.duplicate',
            ),
          }
        }

        if (!data.Datatype) {
          errors[index] = {
            ...errors[index],
            Datatype: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.quantity.errors.required',
            ),
          }
        }
      })

      return errors
    },
    onSubmit: async formData => {},
  })

  const isFormFieldValid = (index: number, name: FormFieldName) =>
    !(
      Array.isArray(formik.touched.customFieldDefinitions) &&
      formik.touched.customFieldDefinitions[index]?.[name] &&
      Array.isArray(formik.errors) &&
      (formik.errors[index] as FormikErrors<CustomFieldDefinition>)?.[name]
    )

  const getFormErrorMessage = (index: number, name: FormFieldName) => {
    // @ts-ignore
    const fieldErrors = formik.errors[index]

    return (
      !isFormFieldValid(index, name) && (
        <small className="p-error">{fieldErrors?.[name]}</small>
      )
    )
  }

  return (
    <SupplierView
      formik={formik}
      customFieldDefinitions={customFieldDefinitions}
      isLoading={isLoading}
      hasLoadingRowId={hasLoadingRowId}
      isBulkDeleteRunning={isBulkDeleteRunning}
      handleCreate={handleCreate}
      handleDelete={handleDelete}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
    />
  )
}
